import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  googleAuth as googleAuthApi,
  appleAuth as appleAuthApi,
  phoneAuth as phoneAuthApi,
  refreshToken as refreshTokenApi,
  logout as logoutApi
} from '@/api/auth'
import { getUserInfo, getMemberAsset } from '@/api/user'
import { encrypt } from '@/utils/crypto'
import _omit from 'lodash/omit'
import { DAY_OF_SECONDS, OAUTH_ACTION_TYPE } from '@/common/constants'
import { setApmUserId, clearApmUserId } from '@/init'
import { clearGioUserId, setGioUserId } from '@/utils/tracker'
import { addGlobalCallbackParameters, clearGlobalCallbackParameters,
  trackAdjustRegister as _trackAdjustRegister } from '@/utils/adjust'
import { t } from '@/locales'
import router from '@/router'


const trackAdjustRegister = data => {
  if (data.action === OAUTH_ACTION_TYPE.REGISTER) {
    _trackAdjustRegister({
      callbackParams: [
        {
          key: 'member_id',
          value: String(data.member_id),
        },
      ],
    })
  }
}

export const useUserStore = defineStore(
  'user',
  () => {

    /**
     * 双 Token 认证方案：
     * - accessToken: 短时效（30分钟内），存储在内存/sessionStorage
     * - refreshToken: 长时效（约7天），存储在 HTTP-only cookie（由服务端设置）
     *
     * ⚠️ accessToken 不再持久化到 localStorage，避免 XSS 攻击
     */
    const accessToken = ref()

    /**
     * config: 登录接口返回的基础配置信息
     * 包含: member_id, action, vip_info 等基础字段
     */
    const config = ref()

    /**
     * userInfo: 完整的用户信息（从 /members/v1/info 接口获取）
     * 包含: nickname, avatar, age, sex, nation 等详细信息
     */
    const userInfo = ref()

    /**
     * IM 相关数据
     * - zy_token: 中台 IM Token
     * - zy_im_config: 中台 IM 配置（解析后的对象）
     * - zy_im_open: IM 是否开启
     */
    const imData = ref()

    /**
     * 会员资产
     */
    const memberAsset = ref()

    const isLoggedIn = computed(() => !!accessToken.value)

    const vipRemainDays = computed(() => {
      const expireAt = userInfo.value?.vip_info?.vip_expire_at
      if (!expireAt) return 0

      // expireAt 是 Unix 时间戳（秒）
      const now = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
      const diff = expireAt - now // 时间差（秒）

      // 转换为天数，如果已过期则返回 0
      const days = Math.max(0, Math.ceil(diff / DAY_OF_SECONDS))
      return days
    })

    /**
     * 设置会员资产
     * @param {Object} asset - 会员资产对象
     */
    function setMemberAsset(asset) {
      memberAsset.value = asset || {}
    }

    /**
     * 设置配置信息（登录接口返回的基础信息）
     */
    function setConfig(configData) {
      config.value = configData
    }

    /**
     * 设置完整的用户信息
     */
    function setUserInfo(info) {
      userInfo.value = info
    }

    /**
     * 获取member_id（优先从config，其次从userInfo）
     */
    function getMemberId() {
      return config.value?.member_id || userInfo.value?.member_id
    }

    /**
     * 获取用户ID（加密后的 memberId ） (优先从config，其次从userInfo)
     */
    function getId() {
      return config.value?.id || userInfo.value?.id
    }

    /**
     * 设置 access token
     */
    function setAccessToken(newToken) {
      accessToken.value = newToken
    }

    /**
     * 设置 IM 数据
     * @param {Object} data - IM 数据对象
     * @param {string} [data.zy_token] - 中台 IM Token
     * @param {string} [data.zy_im_config] - 中台 IM 配置
     * @param {boolean} [data.zy_im_open] - IM 是否开启
     */
    function setImData(data) {
      if (!data) {
        imData.value = null
        return
      }

      let parsedConfig = null
      if (data.zy_im_config) {
        try {
          parsedConfig = typeof data.zy_im_config === 'string'
            ? JSON.parse(data.zy_im_config)
            : data.zy_im_config
        } catch (error) {
          console.error('[IM Data] Failed to parse zy_im_config:', error)
          parsedConfig = null
        }
      }

      imData.value = {
        zy_token: data.zy_token || null,
        zy_im_config: parsedConfig,
        zy_im_open: data.zy_im_open ?? false,
      }
    }

    /**
     * 获取 IM 数据
     * @returns {Object} IM 数据对象
     */
    function getImData() {
      return imData.value
    }

    /**
     * 处理登录响应数据
     * 保存 access token、IM 数据和配置信息
     * @param {Object} data - 登录/刷新 token 的响应数据
     * @param {string} data.access_token - Access Token
     * @param {string} data.member_id - 用户ID
     * @param {string} [data.zy_token] - 中台 IM Token
     * @param {string} [data.zy_im_config] - 中台 IM 配置
     * @param {boolean} [data.zy_im_open] - IM 是否开启
     * @returns {void}
     */
    function handleAuthResponse(data) {
      if (!data?.access_token) {
        return
      }

      const { zy_im_config, zy_im_open, zy_token, access_token } = data

      // 保存 access token
      setAccessToken(access_token)

      // 提取并保存 IM 相关数据（如果服务端返回）
      if (zy_im_config && zy_im_open && zy_token) {
        setImData({
          zy_token,
          zy_im_config,
          zy_im_open,
        })
      }

      // 保存配置信息（排除 access_token 和 IM 相关字段）
      setConfig(_omit(data, ['access_token', 'zy_token', 'zy_im_config', 'zy_im_open']))
    }

    /**
     * 获取并保存完整的用户信息
     * 登录成功后调用此方法获取用户详细信息
     * @returns {Promise<boolean>} 是否获取成功
     */
    async function fetchUserInfo(config = {}) {
      const id = getId()
      if (!id) {
        return false
      }

      try {
        const userInfoData = await getUserInfo({
          member_id: id,
          scene: null
        }, config)

        if (userInfoData) {
          setUserInfo(userInfoData)
          return true
        }

        return false
      } catch (error) {
        return false
      }
    }

    /**
     * 获取会员资产
     */
    async function fetchMemberAsset(config = {}) {
      const data = await getMemberAsset({}, config)
      setMemberAsset(data)
    }

    /**
     * 退出登录
     * @param {boolean} failureAlsoClearState - 退出登录失败时也清除登录状态
     */
    async function logout(failureAlsoClearState = false) {
      try {
        // 调用服务端登出接口，清除 refresh token cookie
        await logoutApi()

        // 清除本地状态
        clearLoginState()

      } catch (error) {
        if (failureAlsoClearState) {
          clearLoginState()
        }
      }
    }

    async function clearLoginState() {
      // 清除 GrowingIO 用户 ID
      clearGioUserId()
      // 清除统一回传参数
      clearGlobalCallbackParameters()
      // 清除 APM 用户 ID
      clearApmUserId()
      // 清除 access token
      setAccessToken('')
      // 清除本地状态
      setConfig(null)
      setUserInfo(null)
      setMemberAsset(null)
      // 清除 IM 数据
      setImData(null)

      // 回到首页
      router.replace('/')
    }

    /**
     * 刷新 access token
     * 使用 refresh token（存储在 HTTP-only cookie）刷新
     * @returns {Promise<boolean>} 是否刷新成功
     */
    async function refreshAccessToken(config) {
      try {
        const data = await refreshTokenApi({
          showLoginModal: true,
          ...config,
        })

        if (data?.access_token) {
          handleAuthResponse(data)
          return true
        }

        return false
      } catch (error) {
        // refresh token 过期或无效，清除登录状态
        await logout(true)
        return false
      }
    }

    /**
     * 检查登录状态
     */
    function checkLoginStatus() {
      return !!accessToken.value
    }

    /**
     * 初始化用户状态
     * 页面加载时调用，尝试使用 refresh token 恢复登录状态
     */
    async function initUserState() {
      if (!accessToken.value && !config.value && !userInfo.value && !imData.value) {
        return false
      }

      // 只是用户信息没有，无需刷新，只获取用户信息
      if (accessToken.value && config.value && imData.value) {
        Promise.all([fetchUserInfo({ hideLoading: true }), fetchMemberAsset({ hideLoading: true })])
        return true
      }

      // accessToken, config 和 imData 其中一个没有，则需要刷新 token
      const refreshed = await refreshAccessToken({ showLoginModal: false })

      // 刷新成功后，获取完整用户信息
      if (refreshed) {
        Promise.all([fetchUserInfo({ hideLoading: true }), fetchMemberAsset({ hideLoading: true })])
      }

      return refreshed
    }

    /**
     * Google 登录
     * @param {Object} authResult - Google 认证结果对象
     * @param {string} authResult.type - 认证类型：'id_token' | 'oauth'
     * @param {string} [authResult.credential] - One Tap 方式返回的 id_token (JWT)
     * @param {string} [authResult.code] - OAuth 方式返回的 authorization code
     * @param {string} [authResult.code_verifier] - OAuth 方式的 PKCE code_verifier
     * @param {string} [authResult.nonce] - One Tap 方式的 nonce
     * @returns {Promise} { action: 'login' | 'new_register', ... }
     */
    async function loginWithGoogle(authResult) {
      console.log('[Google Login] Calling backend API for authentication', authResult)

      const data = await googleAuthApi({
        ...authResult,
        action: OAUTH_ACTION_TYPE.LOGIN,
      })

      // 新用户注册上报 Adjust 事件
      trackAdjustRegister(data)

      // data 中包含：access_token, user (id, member_id, nickname, avatar, age, sex, vip_info 等)
      if (data?.access_token) {
        // 保存 access token、IM 数据和配置信息
        // refresh token 由服务端通过 Set-Cookie 自动设置
        handleAuthResponse(data)

        // 返回 action 类型（login 或 new_register）
        return { action: data.action || OAUTH_ACTION_TYPE.LOGIN }
      }

      throw new Error(t('login.failed'))
    }

    /**
     * Apple 登录
     * @param {Object} authResult - Apple 认证结果对象
     * @param {Object} authResult.authorization - authorization object
     * @param {string} authResult.authorization.code - authorization code
     * @param {string} authResult.authorization.id_token - ID Token
     * @param {Object} authResult.user - User object
     * @param {string} authResult.user.email - Email
     * @param {Object} authResult.user.name - Name object
     * @param {string} authResult.user.name.firstName - First name
     * @param {string} authResult.user.name.lastName - Last name
     * @param {string} authResult.nonce - Nonce
     * @returns {Promise} { action: 'login' | 'new_register', ... }
     */
    async function loginWithApple(authResult) {
      const { authorization = {}, user, nonce } = authResult
      const authData = {
        action: OAUTH_ACTION_TYPE.LOGIN,
        code: authorization.code,
        user,
        nonce,
        token: authorization.id_token,
      }

      const data = await appleAuthApi(authData)

      // 新用户注册上报 Adjust 事件
      trackAdjustRegister(data)

      if (data?.access_token) {
        // 保存 access token、IM 数据和配置信息
        handleAuthResponse(data)

        // 返回 action 类型
        return { action: data.action || OAUTH_ACTION_TYPE.LOGIN }
      }

      throw new Error(t('login.failed'))
    }

    /**
     * 手机号登录（仅测试环境）
     * @param {Object} params - 登录参数
     * @param {string} params.phone - 手机号（明文）
     * @param {string} params.captcha - 验证码
     * @returns {Promise} { action: 'login' | 'new_register', ... }
     */
    async function loginWithPhone({ phone, captcha }) {
      // 加密手机号
      const encryptedPhone = encrypt(phone)

      const data = await phoneAuthApi({
        phone: encryptedPhone,
        captcha,
        action: OAUTH_ACTION_TYPE.LOGIN,
      })

      // 新用户注册上报 Adjust 事件
      trackAdjustRegister(data)

      if (data?.access_token) {
        // 保存 access token、IM 数据和配置信息
        handleAuthResponse(data)

        // 返回 action 类型
        return { action: data.action || OAUTH_ACTION_TYPE.LOGIN }
      }

      throw new Error(t('login.failed'))
    }

    // 监听登录状态变化，自动设置/清除 APM 用户 ID
    watch(isLoggedIn, (newValue) => {
      if (newValue) {
        // 用户已登录，设置 APM 用户 ID
        const userId = getId()
        const memberId = getMemberId()
        if (memberId) {
          addGlobalCallbackParameters([{
            key: 'member_id',
            value: String(memberId),
          }])
        }
        if (userId) {
          setApmUserId(userId)
          setGioUserId(userId)
        }
      }
    })

    return {
      ACTION_TYPE: OAUTH_ACTION_TYPE,
      // Token
      accessToken,
      // Config & User
      config,
      userInfo,
      vipRemainDays,
      memberAsset,
      // IM Data
      imData,
      isLoggedIn,
      // Methods
      setAccessToken,
      setConfig,
      setUserInfo,
      getMemberId,
      getId,
      fetchUserInfo,
      fetchMemberAsset,
      setImData,
      getImData,
      logout,
      checkLoginStatus,
      refreshAccessToken,
      initUserState,
      clearLoginState,
      // Login
      loginWithGoogle,
      loginWithApple,
      loginWithPhone,
    }
  },
  {
    persist: [{
      key: 'USER_CONFIG',
      storage: localStorage,
      // ⚠️ 持久化 config 和 userInfo，不持久化 accessToken 和 IM 数据
      pick: ['config', 'userInfo', 'memberAsset']
    }, {
      key: 'USER_SESSION',
      // accessToken 和 imData 存储在 sessionStorage，避免 XSS 攻击
      pick: ['accessToken', 'imData'],
      storage: sessionStorage,
    }],
  }
)
